import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { api } from '~/trpc-extra/api';
import {
  autoHealStepAtom,
  timeoutAtom,
  recorderConsoleLogsAtom,
  recorderNetworkLogsAtom,
  recorderPausedAtom,
  recordTypeAtom,
  completedItemsAtom,
  conditionalBlockResultsAtom,
  createdDummyStepsAtom,
} from '../../atoms/recorder-atoms';
import { useLiveStateManager } from '../useLiveStateManager';
import { useResetAllPrevStepStoredInTemp } from './useResetAllPrevStepStoredInTemp';
import { useSaveAiProgress } from './useSaveAiProgress';
import { useSendVmCommand } from './useSendVmCommand';
import { useDeleteSteps } from '../mutations/steps/useDeleteSteps';

// This terminates the VM and cleans up any partial results,
// and now also resets all prevStepStoredInTemp values.
export const useResetRecorderAtoms = (testContentId: string) => {
  const setRecorderPaused = useSetAtom(recorderPausedAtom);
  const setRecorderConsoleLogs = useSetAtom(recorderConsoleLogsAtom);
  const setRecorderNetworkLogs = useSetAtom(recorderNetworkLogsAtom);
  const setCompletedItems = useSetAtom(completedItemsAtom);
  // TODO: don't actually reset the AI logs here because maybe users want to review it.
  const setRecordType = useSetAtom(recordTypeAtom);
  const { vmStatus, updateLiveState: updateLiveTestState } = useLiveStateManager();
  const sendVmCommand = useSendVmCommand();
  const resetAutoHealStep = useResetAtom(autoHealStepAtom);
  const resetTimeout = useResetAtom(timeoutAtom);
  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const saveAiProgress = useSaveAiProgress({ testContentId });
  const resetAllPrevStepStoredInTemp = useResetAllPrevStepStoredInTemp();
  const setConditionalBlockResults = useSetAtom(conditionalBlockResultsAtom);
  const createdDummySteps = useAtomValue(createdDummyStepsAtom);
  const resetCreatedDummySteps = useResetAtom(createdDummyStepsAtom);
  const { mutateAsync: deleteSteps } = useDeleteSteps({ testContentId });

  return async () => {
    const operationId = vmStatus?.executionState?.operationId;
    const step = testContent?.steps.find(
      (step) => step.id === operationId || step.operations.some((op) => op.id === operationId),
    );

    try {
      await sendVmCommand({ type: 'terminate' });
    } catch (e) {
      // ignore errors
    }

    // Clean up empty dummy steps that were created during recording
    // Don't await this to avoid blocking the VM shutdown
    if (createdDummySteps.length > 0 && testContent?.steps) {
      const emptyDummyStepIds = createdDummySteps.filter((stepId) => {
        const step = testContent.steps.find((s) => s.id === stepId);
        return step?.mode === 'AI_FALLBACK' && step.description === '' && step.operations.length === 0;
      });

      if (emptyDummyStepIds.length > 0) {
        // Clean up dummy steps in the background without blocking UI reset
        deleteSteps({ stepIds: emptyDummyStepIds }).catch(() => {
          // Ignore delete errors during VM reset
        });
        // Reset the tracking immediately since the UI update is optimistic
        resetCreatedDummySteps();
      }
    }

    // setRecorderInitiator(undefined);
    // setRecorderCursor(undefined);
    setCompletedItems({ type: 'reset' });
    setRecorderPaused(false);
    setRecorderConsoleLogs([]);
    setRecorderNetworkLogs([]);
    updateLiveTestState(() => ({ vmStatus: undefined, recorderCursor: undefined, recorderInitiator: undefined }));
    resetAutoHealStep();
    resetTimeout();
    setRecordType('action');
    setConditionalBlockResults({});
    if (step) {
      await saveAiProgress(step.id);
    }
    // Must be after saveAiProgress. The saveAiProgress depends on the prevStepStoredInTemp values.
    resetAllPrevStepStoredInTemp();
  };
};
